import React, { useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Button,
    Container,
    Paper,
    Alert,
    Snackbar,
    useTheme,
    Divider,
    Backdrop,
    CircularProgress,
} from "@mui/material";
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    Send as SendIcon,
    AccessTime as TimeIcon,
} from "@mui/icons-material";
import contactInfo from "../data/contact";
import { sendContactEmail } from "../utils/emailService";

const Contact = () => {
    const theme = useTheme();

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await sendContactEmail(formData);

            setSnackbar({
                open: true,
                message:
                    "Thank you for your message! We will get back to you soon.",
                severity: "success",
            });
            setFormData({
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message:
                    error.message ||
                    "Failed to send message. Please try again later.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const contactInfoCards = [
        {
            icon: (
                <PhoneIcon
                    sx={{ fontSize: 32, color: theme.palette.secondary.main }}
                />
            ),
            title: "Phone",
            details: contactInfo.phones.map(
                (phone) => `${phone.number} (${phone.label})`
            ),
            color: theme.palette.primary.main,
        },
        {
            icon: (
                <EmailIcon
                    sx={{ fontSize: 32, color: theme.palette.secondary.main }}
                />
            ),
            title: "Email",
            details: contactInfo.emails,
            color: theme.palette.secondary.main,
        },
        {
            icon: (
                <TimeIcon
                    sx={{ fontSize: 32, color: theme.palette.secondary.main }}
                />
            ),
            title: "Business Hours",
            details: contactInfo.businessHours,
            color: theme.palette.secondary.main,
        },
    ];

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: `linear-gradient(120deg, ${theme.palette.background.default} 60%, ${theme.palette.primary.light}10 100%)`,
                position: "relative",
                py: { xs: 2, md: 6 },
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="stretch">
                    {/* Contact Form (Main Focus) */}
                    <Grid item xs={12} md={7}>
                        <Paper
                            elevation={4}
                            sx={{
                                p: { xs: 3, md: 5 },
                                borderRadius: 4,
                                background: theme.palette.background.paper,
                                boxShadow: theme.shadows[6],
                                position: "relative",
                                overflow: "hidden",
                                minHeight: 480,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                            }}
                        >
                            {/* Loader Overlay */}
                            <Backdrop
                                open={loading}
                                sx={{
                                    color: "#fff",
                                    zIndex: (theme) => theme.zIndex.drawer + 2,
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: 4,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "rgba(0,0,0,0.15)",
                                }}
                            >
                                <CircularProgress color="primary" />
                            </Backdrop>
                            {/* Subtle background shape for vibe */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: -60,
                                    right: -60,
                                    width: 180,
                                    height: 180,
                                    borderRadius: "50%",
                                    background: theme.palette.primary.light,
                                    opacity: 0.12,
                                    zIndex: 0,
                                }}
                            />
                            <Typography
                                variant="h4"
                                color="primary.main"
                                sx={{
                                    fontWeight: 700,
                                    mb: 2,
                                    zIndex: 1,
                                    position: "relative",
                                }}
                            >
                                Send us a Message
                            </Typography>
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{ zIndex: 1, position: "relative" }}
                            >
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            variant="outlined"
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontWeight: 500,
                                                },
                                                required: false,
                                            }}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            variant="outlined"
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontWeight: 500,
                                                },
                                                required: false,
                                            }}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontWeight: 500,
                                                },
                                                required: false,
                                            }}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            required
                                            variant="outlined"
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontWeight: 500,
                                                },
                                                required: false,
                                            }}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            multiline
                                            rows={6}
                                            variant="outlined"
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontWeight: 500,
                                                },
                                                required: false,
                                            }}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            endIcon={<SendIcon />}
                                            sx={{
                                                px: 4,
                                                py: 1.5,
                                                fontSize: "1.1rem",
                                                fontWeight: "bold",
                                                borderRadius: 2,
                                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                                                boxShadow: theme.shadows[2],
                                                "&:hover": {
                                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                                                },
                                            }}
                                            disabled={loading}
                                        >
                                            Send Message
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Contact Info Sidebar */}
                    <Grid item xs={12} md={5}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: { xs: 3, md: 4 },
                                borderRadius: 4,
                                background: theme.palette.background.paper,
                                boxShadow: theme.shadows[2],
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                gap: 3,
                            }}
                        >
                            <Typography
                                variant="h5"
                                color="primary.main"
                                sx={{ fontWeight: 700, mb: 2 }}
                            >
                                Contact Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                {contactInfoCards?.map((info, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 2,
                                                mb: 2,
                                            }}
                                        >
                                            <Box sx={{ mt: 0.5 }}>
                                                {info.icon}
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: info.color,
                                                    }}
                                                >
                                                    {info.title}
                                                </Typography>
                                                {Array.isArray(info.details) ? (
                                                    info.details.map(
                                                        (detail, idx) => (
                                                            <Typography
                                                                key={idx}
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{ mb: 0.5 }}
                                                            >
                                                                {detail}
                                                            </Typography>
                                                        )
                                                    )
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mb: 0.5 }}
                                                    >
                                                        {info.details}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* Success/Error Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                sx={{ mb: 2 }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Contact;
